import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import {
  BesharpTableComponent,
  BesharpTableHeaderDefinition,
  BesharpTableSize,
} from '../../modules/besharp-table/component/besharp-table.component';
import { LogService, LogEntry as LogEntryFromService } from '../../services/log.service';

export type LogEntry = LogEntryFromService;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    BesharpTableComponent,
    ButtonModule,
    ToastModule,
    DialogModule,
  ],
  providers: [MessageService],
  selector: 'app-log-management',
  templateUrl: './log-management.component.html',
  styleUrls: ['./log-management.component.scss'],
})
export class LogManagementComponent implements OnInit {
  eBesharpTableSize = BesharpTableSize;

  datasource: LogEntry[] = [];
  datasourceLength = 0;
  orderColumn: string = 'created_at';
  orderDirection: 'asc' | 'desc' = 'desc';
  filters: { [key: string]: any } = {};
  loading = false;
  pageRows = 10;
  offset = 0;

  showLogDetailsModal = false;
  selectedLog: LogEntry | null = null;

  tableDefinition: BesharpTableHeaderDefinition[] = [
    {
      def: 'createdAt',
      title: 'Date',
      sortable: true,
      columnFilter: false,
      transformer: (date: string) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      },
    },
    { 
      def: 'entity', 
      title: 'Entity', 
      sortable: true,
      columnFilter: true,
      transformer: (entity: string) => {
        if (!entity) return '-';
        return entity.charAt(0).toUpperCase() + entity.slice(1);
      }
    },
    { 
      def: 'operationType', 
      title: 'Operation', 
      sortable: true,
      columnFilter: true,
      transformer: (operation: string) => {
        if (!operation) return '-';
        const operationClass = operation.toLowerCase() === 'deploy' ? 'info' : 
                              operation.toLowerCase() === 'assign' ? 'success' : 
                              operation.toLowerCase() === 'unassign' ? 'warning' : 'secondary';
        return `<span class="badge badge-${operationClass}">${operation.toUpperCase()}</span>`;
      }
    },
    { 
      def: 'userEmail', 
      title: 'User', 
      sortable: true,
      columnFilter: true,
    },
    { 
      def: 'changeDetails', 
      title: 'Details', 
      sortable: false,
      transformer: (details: string) => {
        if (!details) return '-';
        try {
          const parsed = JSON.parse(details);
          if (parsed.machineId) return `Machine: ${parsed.machineId.substring(0, 8)}...`;
          if (parsed.machinesId) return `Machines: ${parsed.machinesId.length} selected`;
          return 'View details';
        } catch {
          return details.length > 50 ? details.substring(0, 50) + '...' : details;
        }
      }
    },
  ];

  private refreshTablesListener: ((event: Event) => void) | null = null;

  constructor(
    private messageService: MessageService,
    private logService: LogService
  ) {}

  ngOnInit(): void {
    this.listLogs(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
    this.setupRefreshListener();
  }

  ngOnDestroy(): void {
    this.removeRefreshListener();
  }

  private setupRefreshListener(): void {
    this.refreshTablesListener = () => {
      this.listLogs(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
    };
    window.addEventListener('refreshTable', this.refreshTablesListener);
  }

  private removeRefreshListener(): void {
    if (this.refreshTablesListener) {
    window.removeEventListener('refreshTable', this.refreshTablesListener);
    }
  }

  globalFilterFields = this.tableDefinition.map((row) => row.def);

  actionDefinition = [
    {
      label: 'Details',
      icon: 'pi pi-info-circle',
      styleClass: 'b',
      callback: (row: any) => this.showLogDetails(row),
    },
  ];

  showLogDetails(log: LogEntry) {
    this.selectedLog = log;
    this.showLogDetailsModal = true;
  }

  closeLogDetailsModal(): void {
    this.showLogDetailsModal = false;
    this.selectedLog = null;
  }

  exportLogs(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'Preparing CSV export...'
    });

    this.logService.getLogs(1000, 0, this.orderColumn, this.orderDirection, this.filters).subscribe({
      next: (response) => {
        this.generateCsvFile(response.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Export Complete',
          detail: `Exported ${response.data.length} logs to CSV`
        });
      },
      error: (error) => {
        console.error('Export failed:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'Failed to export logs'
        });
      }
    });
  }

  private generateCsvFile(logs: LogEntry[]): void {
    const headers = ['ID', 'Date', 'Entity', 'Operation', 'User Email', 'Change Details'];
    const escapeCsvField = (field: string): string => {
      if (field === null || field === undefined) return '""';
      const stringField = String(field);
      return `"${stringField.replace(/"/g, '""')}"`;
    };
    
    const csvRows = logs.map(log => [
      escapeCsvField(log.id),
      escapeCsvField(new Date(log.createdAt).toLocaleString('en-GB')),
      escapeCsvField(log.entity),
      escapeCsvField(log.operationType),
      escapeCsvField(log.userEmail),
      escapeCsvField(log.changeDetails)
    ]);

    const csvContent = [headers.map(h => escapeCsvField(h)), ...csvRows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `logs-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  onGlobalSearch(event: any): void {
    // TODO: Implement server-side global search
  }

  onColumnFilter(event: any): void {
    this.filters = event.filters || {};
    this.listLogs(this.pageRows, 0, this.orderColumn, this.orderDirection, this.filters);
  }

  listLogs(
    limit?: number, 
    offset?: number, 
    orderColumn?: string, 
    orderDirection?: 'asc' | 'desc',
    filters?: { [key: string]: any }
  ): void {
    this.loading = true;
    this.datasource = [];
    this.logService.getLogs(limit, offset, orderColumn, orderDirection, filters).subscribe({
      next: (response) => {
        this.datasource = response.data;
        this.datasourceLength = response.total;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.datasource = [];
        this.datasourceLength = 0;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load logs: ${error.message}`
        });
      }
    });
  }

  onPageChange(event: any) {
    this.pageRows = event.rows;
    this.offset = event.first;
    this.listLogs(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  sortChangedEvent(event: any) {
    const fieldMapping: { [key: string]: string } = {
      'createdAt': 'created_at',
      'entity': 'entity',
      'operationType': 'operation_type',
      'userEmail': 'email',
      'id': 'id'
    };
    
    const frontendField = event.field || 'id';
    this.orderColumn = fieldMapping[frontendField] || 'created_at';
    this.orderDirection = event.order === 1 ? 'asc' : 'desc';
    this.offset = 0;
    this.listLogs(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  clearFilters() {
    this.orderColumn = 'created_at';
    this.orderDirection = 'desc';
    this.filters = {};
    this.offset = 0;
    this.listLogs(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }
}
